import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CurrencyContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Currency options for the selector
const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
];

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [symbol, setSymbol] = useState('$');
  const [rate, setRate] = useState(1);
  const [rates, setRates] = useState({ USD: 1 });
  const [loading, setLoading] = useState(true);

  // Fetch exchange rates on mount
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await axios.get(`${API}/currency/rates`);
        setRates(response.data.rates);
        
        // Try to detect user's currency
        const detectResponse = await axios.get(`${API}/currency/detect`);
        const detectedCurrency = detectResponse.data.currency;
        
        // Check if we have a saved preference
        const savedCurrency = localStorage.getItem('preferredCurrency');
        
        if (savedCurrency && response.data.rates[savedCurrency]) {
          changeCurrency(savedCurrency, response.data.rates);
        } else if (detectedCurrency && response.data.rates[detectedCurrency]) {
          changeCurrency(detectedCurrency, response.data.rates);
        }
      } catch (error) {
        console.error('Error fetching currency rates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  // Change currency
  const changeCurrency = (newCurrency, ratesData = rates) => {
    const currencyOption = CURRENCY_OPTIONS.find(c => c.code === newCurrency);
    if (currencyOption && ratesData[newCurrency]) {
      setCurrency(newCurrency);
      setSymbol(currencyOption.symbol);
      setRate(ratesData[newCurrency]);
      localStorage.setItem('preferredCurrency', newCurrency);
    }
  };

  // Convert USD amount to selected currency
  const convertPrice = (usdAmount) => {
    const converted = usdAmount * rate;
    return converted.toFixed(2);
  };

  // Format price with currency symbol
  const formatPrice = (usdAmount) => {
    const converted = usdAmount * rate;
    // For currencies with large values, don't show decimals
    if (rate > 10) {
      return `${symbol}${Math.round(converted).toLocaleString()}`;
    }
    return `${symbol}${converted.toFixed(2)}`;
  };

  const value = {
    currency,
    symbol,
    rate,
    rates,
    loading,
    currencyOptions: CURRENCY_OPTIONS,
    changeCurrency,
    convertPrice,
    formatPrice,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
